import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

// ─── Shared Document Type ─────────────────────────────────────────────────────
export interface DocumentResponse {
  id: string;
  name: string;
  blobName: string;
  blobFileUrl: string;
  blobSize: number;
  extension: string;
}

// ─── Step 0: Company Info ─────────────────────────────────────────────────────
export interface ProfileInfoResponse {
  payload: {
    id: string;
    name: string;
    bio: string;
    createdAt: string;
    employeesNumber: string;
    foundYear: number;
    introductionVideoUrl: string;
    isVerified: boolean;
    languages: { code: string; name: string }[];
    localTime: string;
    location: string;
    photoUrl: string;
    photo?: {
      id: string;
    };
    photoId?: string;
    profileTypeCode: string;
    websiteLink: string;
  };
}

export interface UpdateProfileInfoRequest {
  bio?: string;
  companySizeId?: string;
  foundedYear?: number;
  introductionVideoUrl?: string;
  websiteLink?: string;
  fileId?: string;
}

export interface UpdateProfileUserInfoRequest {
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
}

// ─── Languages ────────────────────────────────────────────────────────────────
export interface LanguageItem {
  id: string;
  language: { id: number | string; code: string; name: string };
  proficiency: { id: number | string; code: string; name: string };
}

export interface LanguagesResponse {
  payload: LanguageItem[];
}

export interface AddLanguageRequest {
  profileId: string;
  languageId: string;
  proficiencyId: string;
}

export interface UpdateLanguageRequest {
  profileId: string;
  id: string;
  languageId: string;
  proficiencyId: string;
}

// ─── Step 1: Catalogue ────────────────────────────────────────────────────────
export interface CatalogueAttachment {
  id: string;
  type: 'IMAGE' | 'VIDEO';
}

export interface CatalogueItem {
  id?: string;
  name: string;
  description: string;
  specs: string;
  attachments: CatalogueAttachment[];
}

export interface CatalogueResponse {
  payload: CatalogueItem[];
}

export interface AddCatalogueRequest {
  profileId: string;
  data: Omit<CatalogueItem, 'id'>;
}

export interface EditCatalogueRequest {
  profileId: string;
  catalogueId: string;
  data: CatalogueItem;
}

export interface DeleteCatalogueRequest {
  profileId: string;
  catalogueId: string;
}

// ─── Step 2: Categories / Specialties ─────────────────────────────────────────
export interface CategoriesResponse {
  payload: any[]; // using any for now since exact type varies
}

export interface AddSpecialtyRequest {
  profileId: string;
  ids: (number | string)[];
}

export interface AddProfileCategoriesRequest {
  profileId: string;
  categoryIds: number[];
}

// ─── Step 3: Team Members ───────────────────────────────────────────────────
export interface TeamMember {
  id: number;
  firstName: string;
  lastName: string;
  roleCode: string;
}

export interface TeamMembersResponse {
  payload: TeamMember[];
}

export interface RolesResponse {
  payload: any[];
}

export interface AddTeamMemberRequest {
  firstName: string;
  lastName: string;
  email: string;
  roleCode: string;
}

export interface UpdateTeamMemberRequest extends AddTeamMemberRequest {
  id: number;
}

// ─── Step 3: Awards ───────────────────────────────────────────────────────────
export interface AwardAttachment {
  id: string;
}

export interface AwardItem {
  id?: string;
  awardName: string;
  awardYear: string;
  description: string;
  attachments: AwardAttachment[];
}

export interface AwardsResponse {
  payload: AwardItem[];
}

export interface PatchAwardRequest {
  profileId: string;
  data: AwardItem;
}

// ─── Step 4: Branches & Warehouses ───────────────────────────────────────────
export interface LocationItem {
  id?: string;
  name: string;
  address: string;
  lat: number;
  lang: number;
  countryCode: string;
}

export interface LocationsResponse {
  payload: LocationItem[];
}

export interface AddLocationRequest {
  profileId: string;
  data: Omit<LocationItem, 'id'>;
}

export interface UpdateLocationRequest {
  profileId: string;
  data: LocationItem;
}

// ─── Marketplace Setup ────────────────────────────────────────────────────────
export interface MarketplaceTerm {
  id: string;
  code: string;
  title: string;
  order: number;
  warning?: string;
  steps: string[];
}

export interface GetMarketplaceTermsResponse {
  payload: MarketplaceTerm[];
}

export interface CreateMarketplaceRequest {
  marketplaceName: string;
  sliderImagesIds?: string[];
  profilePhotoId?: string;
}

// ─── Lookups ──────────────────────────────────────────────────────────────────
export interface LookupResponse {
  payload: unknown[];
}

// ─── RTK Query Service ────────────────────────────────────────────────────────
export const sellerProfileCreationService = createApi({
  reducerPath: 'sellerProfileCreationService',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'ProfileInfo',
    'Languages',
    'Catalogues',
    'Categories',
    'Awards',
    'Branches',
    'Warehouses',
  ],
  endpoints: (builder) => ({

    getProfileInfo: builder.query<ProfileInfoResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/aggregation-service/v1/profiles/${profileId}/info`,
        method: 'GET',
      }),
      providesTags: ['ProfileInfo'],
    }),

    getCompanyInfo: builder.query<any, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/profile-company/${profileId}/info`,
        method: 'GET',
      }),
      providesTags: ['ProfileInfo'],
    }),

    updateProfileInfo: builder.mutation<void, { profileId: string; data: UpdateProfileInfoRequest }>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/business-seller/profile-company/info/by-admin/${profileId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['ProfileInfo'],
    }),

    updateProfileUserInfo: builder.mutation<void, { profileId: string; data: UpdateProfileUserInfoRequest }>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/business-seller/profile-user/info/by-admin/${profileId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ProfileInfo'],
    }),

    // ── Photo ─────────────────────────────────────────────────────────────────
    uploadPhoto: builder.mutation<{ payload: DocumentResponse[] }, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/profile-azure',
        method: 'POST',
        headers: {
          'ACCEPT-CONTENT-TYPE': 'string',
        },
        body,
      }),
    }),

    getProfilePhoto: builder.query<{ payload: DocumentResponse }, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/photo/by-admin/${profileId}`,
        method: 'GET',
      }),
    }),

    updateProfilePhoto: builder.mutation<void, { profileId: string; photoId: string }>({
      query: ({ profileId, photoId }) => ({
        url: `/profile-service/v1/business-seller/photo/by-admin/${profileId}`,
        method: 'PUT',
        body: { photoId },
      }),
      invalidatesTags: ['ProfileInfo'],
    }),

    // ── Languages ─────────────────────────────────────────────────────────────
    getLanguages: builder.query<LanguagesResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-language/profile/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Languages'],
    }),

    addLanguage: builder.mutation<void, AddLanguageRequest>({
      query: ({ profileId, ...body }) => ({
        url: `/profile-service/v1/profile-language/by-admin/${profileId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Languages'],
    }),

    updateLanguage: builder.mutation<void, UpdateLanguageRequest>({
      query: ({ profileId, ...body }) => ({
        url: `/profile-service/v1/profile-language/by-admin/${profileId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Languages'],
    }),

    deleteLanguage: builder.mutation<void, { deleteId: string }>({
      query: ({ deleteId }) => ({
        url: `/profile-service/v1/profile-language/by-admin/${deleteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Languages'],
    }),

    // ── Step 1: Catalogue ─────────────────────────────────────────────────────
    getCatalogues: builder.query<CatalogueResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/catalogue/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Catalogues'],
    }),

    addCatalogue: builder.mutation<{ payload: string }, AddCatalogueRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/catalogue/by-admin/${profileId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Catalogues'],
    }),

    editCatalogue: builder.mutation<{ payload: string }, EditCatalogueRequest>({
      query: ({ profileId, catalogueId, data }) => ({
        url: `/profile-service/v1/catalogue/by-admin/${catalogueId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Catalogues'],
    }),

    deleteCatalogue: builder.mutation<void, DeleteCatalogueRequest>({
      query: ({ profileId, catalogueId }) => ({
        url: `/profile-service/v1/catalogue/by-admin/${catalogueId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Catalogues'],
    }),

    uploadCatalogueImage: builder.mutation<{ payload: DocumentResponse[] }, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/catalogue/documents/images/by-admin',
        method: 'POST',
        body,
      }),
    }),

    uploadCatalogueVideo: builder.mutation<{ payload: DocumentResponse[] }, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/catalogue/documents/videos/by-admin',
        method: 'POST',
        body,
      }),
    }),

    deleteCatalogueDocument: builder.mutation<void, { documentId: string }>({
      query: ({ documentId }) => ({
        url: `/profile-service/v1/catalogue/documents/by-admin/${documentId}`,
        method: 'DELETE',
      }),
    }),

    // ── Step 2: Categories / Specialties ──────────────────────────────────────
    getUserCategory: builder.query<CategoriesResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-specialties/profile/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),

    addUserCategory: builder.mutation<{ payload: string }, AddSpecialtyRequest>({
      query: ({ profileId, ids }) => ({
        url: `/profile-service/v1/profile-specialties/profile/by-admin/${profileId}`,
        method: 'POST',
        body: ids,
      }),
      invalidatesTags: ['Categories'],
    }),

    deleteUserCategory: builder.mutation<void, { specialtyId: string }>({
      query: ({ specialtyId }) => ({
        url: `/profile-service/v1/profile-specialties/profile/specialties/by-admin/${specialtyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories'],
    }),

    // ─── Company Interests (Categories) ───────────────────────────────────────
    getProfileCategories: builder.query<CategoriesResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-specialties/profile/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Categories'],
    }),

    addProfileCategories: builder.mutation<void, AddProfileCategoriesRequest>({
      query: ({ profileId, categoryIds }) => ({
        url: `/profile-service/v1/profiles/${profileId}/categories/admin`,
        method: 'POST',
        body: { categoryIds },
      }),
      invalidatesTags: ['Categories'],
    }),

    deleteProfileCategory: builder.mutation<void, { categoryId: string }>({
      query: ({ categoryId }) => ({
        url: `/profile-service/v1/profiles/profile-categories/${categoryId}/admin`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories'],
    }),

    // ─── Company Team ─────────────────────────────────────────────────────────
    getRoles: builder.query<RolesResponse, void>({
      query: () => ({
        url: '/profile-service/v1/role/business-roles/admin',
        method: 'GET',
      }),
    }),

    getTeamMembers: builder.query<TeamMembersResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile/business/${profileId}/team-member/admin`,
        method: 'GET',
      }),
      providesTags: ['TeamMembers' as any],
    }),

    addTeamMember: builder.mutation<void, { profileId: string; data: AddTeamMemberRequest }>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile/business/${profileId}/team-member/admin`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TeamMembers' as any],
    }),

    updateTeamMember: builder.mutation<void, { profileId: string; data: UpdateTeamMemberRequest }>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile/business/${profileId}/team-member/admin`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['TeamMembers' as any],
    }),

    deleteTeamMember: builder.mutation<void, { memberId: string }>({
      query: ({ memberId }) => ({
        url: `/profile-service/v1/profile/business/${memberId}/team-member/admin`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeamMembers' as any],
    }),

    // ── Step 3: Awards ────────────────────────────────────────────────────────
    getBusinessAwards: builder.query<AwardsResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-award/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Awards'],
    }),

    addAward: builder.mutation<{ payload: string }, PatchAwardRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile-award/by-admin/${profileId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Awards'],
    }),

    deleteAward: builder.mutation<void, { awardId: string }>({
      query: ({ awardId }) => ({
        url: `/profile-service/v1/profile-award/by-admin/${awardId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Awards'],
    }),

    uploadAwardDocs: builder.mutation<{ payload: DocumentResponse[] }, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/profile-award/documents/by-admin',
        method: 'POST',
        body,
      }),
    }),

    deleteAwardDoc: builder.mutation<void, { documentId: string }>({
      query: ({ documentId }) => ({
        url: `/profile-service/v1/profile-award/documents/by-admin/${documentId}`,
        method: 'DELETE',
      }),
    }),

    // ── Step 4: Branches ──────────────────────────────────────────────────────
    getProfileBranch: builder.query<LocationsResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-branch/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Branches'],
    }),

    addProfileBranch: builder.mutation<void, AddLocationRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile-branch/by-admin/${profileId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Branches'],
    }),

    editProfileBranch: builder.mutation<void, UpdateLocationRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile-branch/by-admin`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Branches'],
    }),

    deleteProfileBranch: builder.mutation<void, { branchId: string }>({
      query: ({ branchId }) => ({
        url: `/profile-service/v1/profile-branch/by-admin/${branchId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Branches'],
    }),

    // ── Step 4: Warehouses ────────────────────────────────────────────────────
    getProfileWarehouse: builder.query<LocationsResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/profile-warehouse/${profileId}`,
        method: 'GET',
      }),
      providesTags: ['Warehouses'],
    }),

    addProfileWarehouse: builder.mutation<void, AddLocationRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile-warehouse/by-admin/${profileId}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Warehouses'],
    }),

    editProfileWarehouse: builder.mutation<void, UpdateLocationRequest>({
      query: ({ profileId, data }) => ({
        url: `/profile-service/v1/profile-warehouse/by-admin`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Warehouses'],
    }),

    deleteProfileWarehouse: builder.mutation<void, { warehouseId: string }>({
      query: ({ warehouseId }) => ({
        url: `/profile-service/v1/profile-warehouse/by-admin/${warehouseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Warehouses'],
    }),

    // ── Step 5: Review / Complete ─────────────────────────────────────────────
    getProfilePreview: builder.query<ProfileInfoResponse, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/profile-company/${profileId}/info/${profileId}/admin`,
        method: 'GET',
      }),
    }),

    saveProfileBusiness: builder.mutation<void, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/complete/by-admin/${profileId}`,
        method: 'PUT',
      }),
    }),

    // ── General / Lookups ───────────────
    uploadAzure: builder.mutation<{ payload: DocumentResponse[] }, FormData>({
      query: (body) => ({
        url: '/profile-service/v1/profile-azure',
        method: 'POST',
        headers: {
          'ACCEPT-CONTENT-TYPE': 'string',
        },
        body,
      }),
    }),

    getCompanySizes: builder.query<LookupResponse, void>({
      query: () => ({
        url: '/profile-service/v1/company-size',
        method: 'GET',
      }),
    }),
    getLanguagesLk: builder.query<LookupResponse, void>({
      query: () => ({
        url: '/lookup-service/v1/languages',
        method: 'GET',
      }),
    }),
    getProficienciesLk: builder.query<LookupResponse, void>({
      query: () => ({
        url: '/lookup-service/v1/proficiencies',
        method: 'GET',
      }),
    }),
    getCategoriesLk: builder.query<LookupResponse, { name?: string }>({
      query: (params) => ({
        url: '/lookup-service/v1/categories',
        method: 'GET',
        params,
      }),
    }),
    getCategorySpecialtiesLk: builder.query<LookupResponse, { categoryId?: string }>({
      query: ({ categoryId }) => ({
        url: '/lookup-service/v1/specialities',
        method: 'GET',
        params: { 'category-id': categoryId },
      }),
    }),
    getPercentage: builder.query<{ payload: number }, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/percentage/${profileId}`,
        method: 'GET',
      }),
    }),
    getMarketplaceTerms: builder.query<GetMarketplaceTermsResponse, void>({
      query: () => ({
        url: '/profile-service/v1/marketplace-terms',
        method: 'GET',
      }),
    }),
    addSliderImages: builder.mutation<{ payload: DocumentResponse[] }, { profileId: string; body: FormData }>({
      query: ({ profileId, body }) => ({
        url: `/profile-service/v1/business-seller/${profileId}/slider-images/by-admin`,
        method: 'POST',
        body,
      }),
    }),
    getSliderImages: builder.query<{ payload: DocumentResponse[] }, { profileId: string }>({
      query: ({ profileId }) => ({
        url: `/profile-service/v1/business-seller/${profileId}/slider/by-admin`,
        method: 'GET',
      }),
    }),
    createMarketplace: builder.mutation<void, { profileId: string; data: CreateMarketplaceRequest }>({
      query: ({ profileId, data }) => ({
        url: `/marketplace-service/v1/business-seller/${profileId}/marketplace/by-admin`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ProfileInfo'],
    }),
  }),
});

export const {
  // Company Info
  useGetProfileInfoQuery,
  useGetCompanyInfoQuery,
  useUpdateProfileInfoMutation,
  useUpdateProfileUserInfoMutation,
  // Photo
  useUploadPhotoMutation,
  useGetProfilePhotoQuery,
  useUpdateProfilePhotoMutation,
  // Languages
  useGetLanguagesQuery,
  useAddLanguageMutation,
  useUpdateLanguageMutation,
  useDeleteLanguageMutation,
  // Catalogue
  useGetCataloguesQuery,
  useAddCatalogueMutation,
  useEditCatalogueMutation,
  useDeleteCatalogueMutation,
  useUploadCatalogueImageMutation,
  useUploadCatalogueVideoMutation,
  useDeleteCatalogueDocumentMutation,
  // Categories / Specialties
  useGetUserCategoryQuery,
  useAddUserCategoryMutation,
  useDeleteUserCategoryMutation,
  useGetProfileCategoriesQuery,
  useAddProfileCategoriesMutation,
  useDeleteProfileCategoryMutation,
  // Team Members
  useGetRolesQuery,
  useGetTeamMembersQuery,
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  // Awards
  useGetBusinessAwardsQuery,
  useAddAwardMutation,
  useDeleteAwardMutation,
  useUploadAwardDocsMutation,
  useDeleteAwardDocMutation,
  // Branches
  useGetProfileBranchQuery,
  useAddProfileBranchMutation,
  useEditProfileBranchMutation,
  useDeleteProfileBranchMutation,
  // Warehouses
  useGetProfileWarehouseQuery,
  useAddProfileWarehouseMutation,
  useEditProfileWarehouseMutation,
  useDeleteProfileWarehouseMutation,
  // Review / Complete
  useGetProfilePreviewQuery,
  useSaveProfileBusinessMutation,
  // General
  useUploadAzureMutation,
  useGetCompanySizesQuery,
  useGetLanguagesLkQuery,
  useGetProficienciesLkQuery,
  useGetCategoriesLkQuery,
  useGetCategorySpecialtiesLkQuery,
  useGetPercentageQuery,
  useGetMarketplaceTermsQuery,
  useAddSliderImagesMutation,
  useGetSliderImagesQuery,
  useCreateMarketplaceMutation,
} = sellerProfileCreationService;
